import React, { useCallback, useState } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";
import { Button, Popconfirm, TextAreaWithMentions } from "@mapmycustomers/ui";

import { getAllTeamsOfEntireOrganization, getUsersOfEntireOrganization } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";

import styles from "./EditNoteForm.module.scss";

interface Props {
  allUsers: User[];
  isDeleting?: boolean;
  isSaving?: boolean;
  noteText?: string;
  onCancel: () => void;
  onDelete?: () => void;
  onSave?: (noteText: string) => void;
  teams: Team[];
}

const EditNoteForm: React.FC<Props> = ({
  allUsers,
  isDeleting,
  isSaving,
  noteText,
  onCancel,
  onDelete,
  onSave,
  teams,
}) => {
  const intl = useIntl();
  const [text, setText] = useState(noteText ?? "");

  const handleSaveClick = useCallback(() => {
    onSave?.(text);
    onCancel();
  }, [text, onCancel, onSave]);

  return (
    <div>
      <div>
        <TextAreaWithMentions
          allUsers={allUsers}
          disabled={isSaving}
          hideDisabledUsers
          maxLength={Number.MAX_SAFE_INTEGER}
          onChange={setText}
          teams={teams}
          value={text}
        />
      </div>
      <div className={styles.noteCreatingActions}>
        <Button loading={isSaving} onClick={handleSaveClick} type="primary">
          {intl.formatMessage({
            id: "recordPane.editNotes.save",
            defaultMessage: "Save Note",
            description: "Save button label for notes section in a Preview pane",
          })}
        </Button>
        <Button disabled={isSaving} onClick={onCancel}>
          {intl.formatMessage({
            id: "recordPane.editNotes.cancel",
            defaultMessage: "Cancel",
            description: "Cancel button label for notes section in a Preview pane",
          })}
        </Button>
        {!!onDelete && (
          <Popconfirm
            cancelText={intl.formatMessage({
              id: "recordPane.editNotes.confirm.cancel",
              defaultMessage: "Cancel",
              description: "Confirm cancel text for remove a note in a Preview pane",
            })}
            description={intl.formatMessage({
              id: "recordPane.editNotes.confirm.title",
              defaultMessage: "Remove Note?",
              description: "Confirm title text for remove a note in a Preview pane",
            })}
            okText={intl.formatMessage({
              id: "recordPane.editNotes.confirm.ok",
              defaultMessage: "Yes",
              description: "Confirm ok text for remove a note in a Preview pane",
            })}
            onConfirm={onDelete}
          >
            <Button className={styles.noteCreatingDeleteAction} danger loading={isDeleting}>
              {intl.formatMessage({
                id: "recordPane.editNotes.delete",
                defaultMessage: "Delete",
                description: "Delete button",
              })}
            </Button>
          </Popconfirm>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  allUsers: getUsersOfEntireOrganization(state),
  teams: getAllTeamsOfEntireOrganization(state),
});

export default connect(mapStateToProps)(EditNoteForm);
